import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Fade from "@mui/material/Fade";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import Grid2 from "@mui/material/Unstable_Grid2";
import { JsonForms } from "@jsonforms/react";
import {
    materialRenderers,
    materialCells,
} from "@jsonforms/material-renderers";
import { useTranslation } from "react-i18next";
import { theme } from "../../../framework/theme";
import { FormTemplateDto } from "../../../models/formTemplate/formTemplateDto";
import * as formTemplateService from "../../../services/formTemplateService";
import { useFetch } from "../../../hooks/useFetch";

interface FormSchemaDialogProps {
    templateId: string;
    onClose: () => void;
}

export const FormSchemaDialog = ({
    templateId,
    onClose,
}: FormSchemaDialogProps) => {
    const { t } = useTranslation();
    const [formTemplate, setFormTemplate] = useState<FormTemplateDto>();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

    useFetch(
        (signal) => formTemplateService.getFormTemplates(false, signal),
        {
            onSuccess: (response) => {
                const template = response.data.find(
                    (ft) => ft.id === templateId
                );
                setFormTemplate(template);
            },
            withLoadingIndicator: false,
        },
        [templateId]
    );

    return (
        <Dialog
            fullScreen={fullScreen}
            open
            maxWidth="md"
            fullWidth
            onClose={onClose}
            TransitionComponent={Fade}
        >
            <DialogTitle>
                <Grid2
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h2">
                        {formTemplate?.name ?? ""}
                    </Typography>
                    <IconButton size="small" color="primary" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Grid2>
            </DialogTitle>
            <DialogContent dividers>
                {!formTemplate ? (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress />
                    </Box>
                ) : !!formTemplate.jsonSchema?.schema ? (
                    <Box sx={{ "& .MuiRadioGroup-root": { mb: 1, order: 1 } }}>
                        <JsonForms
                            schema={formTemplate.jsonSchema.schema}
                            uischema={formTemplate.jsonSchema.uiSchema}
                            data={{}}
                            renderers={materialRenderers}
                            cells={materialCells}
                            config={{ showUnfocusedDescription: true }}
                            validationMode="ValidateAndHide"
                        />
                    </Box>
                ) : (
                    <Alert severity="warning">
                        {t("settings.forms.schemaInvalidOrMissing")}
                    </Alert>
                )}
            </DialogContent>
        </Dialog>
    );
};
